import { getWidgetMergeMap, widgetItemIdentity, } from "./merge-map.js";
import { widgetTargetKey, applyWidgetOperations, emptyWidgetContentMeta, } from "./widget-operations.js";
/**
 * Operation ids have to be stable for a given diff so a retry of the same
 * command carries the same body and hits the receipt rather than being read as
 * a different request. Derived from the target and the operation kind, which
 * are exactly what makes two operations the same operation.
 */
function operationId(kind, targetKey) {
    return `${kind}:${targetKey}`;
}
export function diffWidgetParams(kind, before, after) {
    const map = getWidgetMergeMap(kind);
    const operations = [];
    const unrepresentable = [];
    if (map.disposition !== "operations") {
        // Not an addressing failure — this kind is simply not merged here.
        if (!same(before, after))
            unrepresentable.push("");
        return { operations, unrepresentable, unchanged: same(before, after) };
    }
    const ctx = { map, operations, unrepresentable };
    diffObject(ctx, [], before, after, {
        fields: map.fields,
        groups: map.groups ?? [],
        collections: map.collections ?? [],
        records: [],
        valueSets: map.valueSets ?? [],
        systemFields: map.systemFields ?? [],
    });
    // A move is only visible across the WHOLE tree: the same item id leaving one
    // parent collection and arriving in another. Detected after the structural
    // pass so the add/remove pair it replaces can be withdrawn.
    applyMoveDetection(ctx, map, before, after);
    // Ordering is opt-in: most widgets use arrays only as sets of identified
    // items. SIPOC's five lists are different; their visible order is content.
    appendOrderedMoves(ctx, [], before, after, map.collections ?? []);
    if (kind === "genericTable") {
        const removedColumns = new Set(operations.flatMap((op) => {
            const leaf = op.target[0];
            return op.kind === "remove_item" &&
                leaf?.kind === "item" &&
                leaf.collection === "columns"
                ? [leaf.itemId]
                : [];
        }));
        // Column removal and the corresponding cell deletions must be validated
        // together. Keep unrelated cell edits independent of this structural group.
        for (let i = 0; i < operations.length; i += 1) {
            const op = operations[i];
            const [item, cell] = op.target;
            const removesColumn = op.kind === "remove_item" &&
                item?.kind === "item" &&
                item.collection === "columns";
            const removesCell = op.kind === "set_field" &&
                op.value === undefined &&
                item?.kind === "item" &&
                item.collection === "rows" &&
                cell?.kind === "entry" &&
                cell.record === "cells" &&
                removedColumns.has(cell.key);
            if (removesColumn || removesCell)
                operations[i] = { ...op, groupId: "remove-columns" };
        }
    }
    if (kind === "impactEffort") {
        for (let i = 0; i < operations.length; i += 1) {
            const op = operations[i];
            const [item, field] = op.target;
            if (op.kind === "set_field" &&
                item?.kind === "item" &&
                item.collection === "points" &&
                field?.kind === "prop" &&
                ["impact", "effort"].includes(field.name)) {
                operations[i] = { ...op, groupId: `coordinate:${item.itemId}` };
            }
        }
    }
    return {
        operations,
        unrepresentable,
        unchanged: operations.length === 0 && unrepresentable.length === 0,
    };
}
/** Carry only edits made since `before` onto fresh server content. */
export function rebaseWidgetParams(kind, before, draft, remote) {
    const diff = diffWidgetParams(kind, before, draft);
    if (diff.unchanged)
        return remote;
    // The legacy transport still uses whole-row CAS for unaddressable content.
    if (diff.unrepresentable.length > 0)
        return draft;
    return applyWidgetOperations({
        kind,
        params: remote,
        operations: diff.operations,
        meta: emptyWidgetContentMeta(0),
        source: "human",
        actorId: null,
        now: 0,
    }).params;
}
function diffObject(ctx, path, before, after, level) {
    const from = asRecord(before) ?? {};
    const to = asRecord(after) ?? {};
    for (const field of level.fields) {
        if (same(from[field], to[field]))
            continue;
        push(ctx, {
            kind: "set_field",
            path: [...path, { kind: "prop", name: field }],
            value: to[field],
        });
    }
    for (const group of level.groups) {
        const groupBefore = asRecord(from[group.group]) ?? {};
        const groupAfter = asRecord(to[group.group]) ?? {};
        for (const field of group.fields) {
            if (same(groupBefore[field], groupAfter[field]))
                continue;
            push(ctx, {
                kind: "set_field",
                path: [
                    ...path,
                    { kind: "prop", name: group.group },
                    { kind: "prop", name: field },
                ],
                value: groupAfter[field],
            });
        }
    }
    for (const record of level.records) {
        const recordBefore = asRecord(from[record.record]) ?? {};
        const recordAfter = asRecord(to[record.record]) ?? {};
        for (const key of union(recordBefore, recordAfter)) {
            if (same(recordBefore[key], recordAfter[key]))
                continue;
            push(ctx, {
                kind: "set_field",
                path: [...path, { kind: "entry", record: record.record, key }],
                value: recordAfter[key],
            });
        }
    }
    // A value set is one unit by decision — splitting it would invent an identity
    // the domain does not have — and the executor exposes no operation for it, so
    // a change here is a fallback, not a failure to look harder.
    //
    // The ONE exception is a declared exclusion set, whose members ARE the ids of
    // a mapped collection: parking an idea is addressable per member, so a person
    // parking one describes it the same way an AI proposal does and moves the
    // same revision. Only at the top level, which is the only place an exclusion
    // is declared.
    for (const name of level.valueSets) {
        if (same(from[name], to[name]))
            continue;
        if (path.length === 0 && ctx.map.exclusion?.valueSet === name) {
            if (diffExclusion(ctx, name, from[name], to[name]))
                continue;
        }
        ctx.unrepresentable.push(widgetTargetKey([...path, { kind: "collection", name }]));
    }
    // System metadata must never travel as a content edit, but a change to it is
    // still a real difference the caller has to know it cannot send.
    for (const name of level.systemFields) {
        if (same(from[name], to[name]))
            continue;
        ctx.unrepresentable.push(widgetTargetKey([...path, { kind: "prop", name }]));
    }
    // Anything the map does not account for, that nevertheless CHANGED.
    //
    // Without this the diff is silently lossy: a key the merge map has never
    // heard of simply is not looked at, so dropping it — which is exactly how
    // Tags clears a quadrant, by omitting the key — produces an empty diff and
    // the change is lost. Reporting it sends the whole object down the legacy
    // path instead, which is the behaviour that key has always had.
    const accounted = new Set([
        ...level.fields,
        ...level.groups.map((group) => group.group),
        ...level.records.map((record) => record.record),
        ...level.valueSets,
        ...level.systemFields,
        ...level.collections.map((spec) => spec.collection),
    ]);
    for (const key of union(from, to)) {
        if (accounted.has(key))
            continue;
        if (same(from[key], to[key]))
            continue;
        ctx.unrepresentable.push(widgetTargetKey([...path, { kind: "prop", name: key }]));
    }
    for (const spec of level.collections) {
        diffCollection(ctx, path, from[spec.collection], to[spec.collection], spec);
    }
}
/**
 * Describe a change to the exclusion set as one operation per MEMBER that moved.
 *
 * Returns false when the change cannot be described honestly — a non-array on
 * either side, or a duplicate id, which would make "did this member move" a
 * coin flip — and the caller then falls back exactly as it did before.
 */
function diffExclusion(ctx, name, before, after) {
    if (!Array.isArray(before) || !Array.isArray(after))
        return false;
    const ids = (values) => {
        const set = new Set();
        for (const value of values) {
            if (typeof value !== "string" || value.length === 0)
                return null;
            if (set.has(value))
                return null;
            set.add(value);
        }
        return set;
    };
    const from = ids(before);
    const to = ids(after);
    if (from === null || to === null)
        return false;
    for (const id of to) {
        if (from.has(id))
            continue;
        pushExclusion(ctx, "exclude_item", name, id);
    }
    for (const id of from) {
        if (to.has(id))
            continue;
        pushExclusion(ctx, "include_item", name, id);
    }
    return true;
}
function pushExclusion(ctx, kind, record, key) {
    const target = [{ kind: "entry", record, key }];
    ctx.operations.push({
        operationId: operationId(kind, widgetTargetKey(target)),
        kind,
        target,
    });
}
function diffCollection(ctx, path, before, after, spec) {
    const collectionPath = [
        ...path,
        { kind: "collection", name: spec.collection },
    ];
    const collectionKey = widgetTargetKey(collectionPath);
    const from = Array.isArray(before) ? before : [];
    const to = Array.isArray(after) ? after : [];
    if (same(from, to))
        return;
    // No stable identity: an item-level operation would have to address by
    // position, which is the thing the design forbids.
    if (spec.itemIdField === null) {
        ctx.unrepresentable.push(collectionKey);
        return;
    }
    const idField = spec.itemIdField;
    const beforeById = indexById(from, spec);
    const afterById = indexById(to, spec);
    // A duplicate id makes every later decision a coin flip. Refuse to describe
    // the change rather than pick one.
    if (beforeById === null || afterById === null) {
        ctx.unrepresentable.push(collectionKey);
        return;
    }
    const structural = spec.fixedLength !== undefined &&
        (from.length !== to.length ||
            [...afterById.keys()].some((id) => !beforeById.has(id)));
    if (structural) {
        ctx.unrepresentable.push(collectionKey);
        return;
    }
    for (const [id, item] of afterById) {
        if (beforeById.has(id))
            continue;
        if (spec.fixedLength !== undefined) {
            ctx.unrepresentable.push(collectionKey);
            return;
        }
        push(ctx, {
            kind: "add_item",
            path: collectionPath,
            value: item,
            itemId: id,
        });
    }
    for (const [id] of beforeById) {
        if (afterById.has(id))
            continue;
        if (spec.fixedLength !== undefined) {
            ctx.unrepresentable.push(collectionKey);
            return;
        }
        push(ctx, {
            kind: "remove_item",
            path: [
                ...path,
                { kind: "item", collection: spec.collection, itemId: id },
            ],
        });
    }
    for (const [id, afterItem] of afterById) {
        const beforeItem = beforeById.get(id);
        if (beforeItem === undefined)
            continue;
        if (same(beforeItem, afterItem))
            continue;
        const itemPath = [
            ...path,
            { kind: "item", collection: spec.collection, itemId: id },
        ];
        // An OPAQUE body has no leaves to walk — the whole item is the unit of
        // change. Descending here would report every key as unrepresentable and
        // send the caller down the whole-params path, which is the very thing that
        // made two people editing two different elements overwrite each other.
        if (spec.opaqueItemBody === true) {
            push(ctx, {
                kind: "replace_item",
                path: itemPath,
                value: afterItem,
            });
            continue;
        }
        diffObject(ctx, itemPath, beforeItem, afterItem, {
            // An item value set is a `set_field` target like any other leaf here, so
            // a person rewording one risk's assumptions sends a bounded operation on
            // `risks[<id>]/assumptions` rather than falling through to a whole-params
            // write. That is what makes the AI's baseline for that one target
            // meaningful: the human's edit bumps exactly the revision the proposal
            // was fenced against, so the proposal SKIPS with `field_changed` instead
            // of landing on top of it.
            fields: [...spec.fields, ...(spec.itemValueSets ?? [])],
            groups: [],
            collections: spec.children ?? [],
            records: spec.records ?? [],
            // The item's own id is its ADDRESS, not content: it is accounted for so
            // an unchanged id is not reported, and a CHANGED one is impossible here
            // because the two items were matched by it. A composite key contributes
            // every component for the same reason.
            valueSets: [idField],
            systemFields: [],
        });
    }
}
/**
 * Turn a matching remove/add pair for one id into a single `move_item`.
 *
 * Moving a cause between fishbone categories is one gesture and has to stay one
 * operation: as a remove plus an add it is two independently-skippable halves,
 * and a skipped half loses the cause. Detected globally because the two ends
 * are in different parents.
 */
function applyMoveDetection(ctx, map, before, after) {
    const removes = new Map();
    const adds = new Map();
    ctx.operations.forEach((operation, index) => {
        const leaf = operation.target[operation.target.length - 1];
        if (operation.kind === "remove_item" && leaf?.kind === "item") {
            removes.set(`${leaf.collection} ${leaf.itemId}`, index);
        }
        if (operation.kind === "add_item" && leaf?.kind === "collection") {
            const id = itemIdOf(operation.value, map, operation.target);
            if (id !== null)
                adds.set(`${leaf.name} ${id}`, index);
        }
    });
    const drop = new Set();
    for (const [key, removeIndex] of removes) {
        const addIndex = adds.get(key);
        if (addIndex === undefined)
            continue;
        const removeOp = ctx.operations[removeIndex];
        const addOp = ctx.operations[addIndex];
        // Same collection NAME under a different parent — that is a move. Same
        // parent too would mean the id was removed and re-added identically, which
        // a diff cannot distinguish from no change and never produces.
        if (widgetTargetKey(removeOp.target) === widgetTargetKey(addOp.target)) {
            continue;
        }
        drop.add(removeIndex);
        drop.add(addIndex);
        ctx.operations.push({
            operationId: operationId("move_item", widgetTargetKey(removeOp.target)),
            kind: "move_item",
            target: removeOp.target,
            to: addOp.target,
        });
    }
    if (drop.size === 0)
        return;
    const kept = ctx.operations.filter((_, index) => !drop.has(index));
    ctx.operations.length = 0;
    ctx.operations.push(...kept);
    void before;
    void after;
}
/** Reorder only after structural changes, so each index names the resulting list. */
function appendOrderedMoves(ctx, path, before, after, specs) {
    const from = asRecord(before) ?? {};
    const to = asRecord(after) ?? {};
    for (const spec of specs) {
        const beforeItems = Array.isArray(from[spec.collection])
            ? from[spec.collection]
            : [];
        const afterItems = Array.isArray(to[spec.collection])
            ? to[spec.collection]
            : [];
        const collectionPath = [
            ...path,
            { kind: "collection", name: spec.collection },
        ];
        const collectionKey = widgetTargetKey(collectionPath);
        const beforeById = indexById(beforeItems, spec);
        const afterById = indexById(afterItems, spec);
        if (spec.ordered && beforeById && afterById) {
            const working = [...beforeById.keys()];
            for (const op of ctx.operations) {
                const leaf = op.target[op.target.length - 1];
                const targetKey = widgetTargetKey(op.target);
                if (op.kind === "add_item" && targetKey === collectionKey) {
                    const id = widgetItemIdentity(spec, op.value);
                    if (id !== null)
                        working.push(id);
                }
                else if ((op.kind === "remove_item" || op.kind === "move_item") &&
                    leaf?.kind === "item" &&
                    widgetTargetKey([
                        ...op.target.slice(0, -1),
                        { kind: "collection", name: leaf.collection },
                    ]) === collectionKey) {
                    const index = working.indexOf(leaf.itemId);
                    if (index >= 0)
                        working.splice(index, 1);
                }
                if (op.kind === "move_item" && op.to) {
                    if (widgetTargetKey(op.to) === collectionKey &&
                        leaf?.kind === "item") {
                        working.splice(op.toIndex ?? working.length, 0, leaf.itemId);
                    }
                }
            }
            const desired = [...afterById.keys()];
            if (working.length !== desired.length ||
                desired.some((id) => !working.includes(id))) {
                ctx.unrepresentable.push(collectionKey);
            }
            else {
                for (let index = 0; index < desired.length; index += 1) {
                    const id = desired[index];
                    const currentIndex = working.indexOf(id);
                    if (currentIndex === index)
                        continue;
                    working.splice(currentIndex, 1);
                    working.splice(index, 0, id);
                    const target = [
                        ...path,
                        { kind: "item", collection: spec.collection, itemId: id },
                    ];
                    ctx.operations.push({
                        operationId: `${operationId("move_item", widgetTargetKey(target))}:order:${index}`,
                        kind: "move_item",
                        target,
                        to: collectionPath,
                        toIndex: index,
                    });
                }
            }
        }
        for (const [id, afterItem] of afterById ?? []) {
            const beforeItem = beforeById?.get(id);
            if (beforeItem === undefined)
                continue;
            appendOrderedMoves(ctx, [...path, { kind: "item", collection: spec.collection, itemId: id }], beforeItem, afterItem, spec.children ?? []);
        }
    }
}
function itemIdOf(value, map, collectionPath) {
    const spec = findSpec(map.collections ?? [], collectionPath);
    if (spec === null)
        return null;
    return widgetItemIdentity(spec, value);
}
function findSpec(collections, path) {
    let current = collections;
    let found = null;
    for (const segment of path) {
        const name = segment.kind === "collection"
            ? segment.name
            : segment.kind === "item"
                ? segment.collection
                : null;
        if (name === null)
            return null;
        const spec = current.find((entry) => entry.collection === name);
        if (!spec)
            return null;
        found = spec;
        current = spec.children ?? [];
    }
    return found;
}
function push(ctx, input) {
    const targetKey = widgetTargetKey(input.path);
    // An addition's target is the COLLECTION, so the collection key alone would
    // give two additions to the same collection the same id — and a caller that
    // coalesces successive diffs by id (the SOLVE resolver does, to accumulate a
    // debounced edit) would then keep only the last one and silently drop the
    // other item. The new item's id is what distinguishes them.
    const id = input.kind === "add_item"
        ? operationId(input.kind, `${targetKey}[${input.itemId}]`)
        : operationId(input.kind, targetKey);
    const base = {
        operationId: id,
        kind: input.kind,
        target: input.path,
    };
    ctx.operations.push(input.kind === "remove_item"
        ? base
        : { ...base, value: structuredClone(input.value) });
}
/** `null` when an id repeats — the caller must not guess which item is meant. */
function indexById(items, spec) {
    const byId = new Map();
    for (const item of items) {
        const id = widgetItemIdentity(spec, item);
        if (id === null)
            return null;
        if (byId.has(id))
            return null;
        byId.set(id, item);
    }
    return byId;
}
function union(left, right) {
    return [...new Set([...Object.keys(left), ...Object.keys(right)])].sort();
}
/**
 * Structural equality, and it has to be structural rather than referential:
 * several editors rebuild an array on every keystroke, so reference identity
 * would report every collection as changed on every save — which is exactly the
 * whole-object behaviour this function exists to avoid.
 */
function same(left, right) {
    if (left === right)
        return true;
    if (left === undefined || right === undefined)
        return false;
    return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}
function asRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? value
        : undefined;
}
//# sourceMappingURL=widget-diff.js.map